import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { RebacExplorerClient, StatusResponse, StatusMode } from '../../api/RebacExplorerClient';

type ModeConfig = {
  label: string;
  iconColor: string;
  chipBg: string;
  chipText: string;
  borderColor: string;
  description: string;
};

const MODE_CONFIG: Record<StatusMode, ModeConfig> = {
  rbac: {
    label: 'Apenas RBAC',
    iconColor: '#9e9e9e',
    chipBg: '#616161',
    chipText: '#ffffff',
    borderColor: '#9e9e9e',
    description: 'OpenFGA não está configurado nesta instância. Todas as decisões de autorização são tomadas exclusivamente pelo RBAC interno.',
  },
  shadow: {
    label: 'ReBAC — Shadow Mode',
    iconColor: '#ff8f00',
    chipBg: '#e65100',
    chipText: '#ffffff',
    borderColor: '#ffb300',
    description: 'O RBAC é autoritativo. O OpenFGA executa em paralelo, registrando divergências nos logs, mas sem afetar decisões de acesso. Fase de observação e calibração do modelo.',
  },
  enforce: {
    label: 'ReBAC — Enforce Mode',
    iconColor: '#43a047',
    chipBg: '#2e7d32',
    chipText: '#ffffff',
    borderColor: '#43a047',
    description: 'O OpenFGA é autoritativo para os plugins listados abaixo. As decisões de acesso são determinadas pelo modelo ReBAC.',
  },
};

function EnforcedPlugins({ enforce }: { enforce: StatusResponse['enforce'] }) {
  const active = Object.entries(enforce).filter(([, v]) => v).map(([k]) => k);
  if (active.length === 0) return null;
  return (
    <Box display="flex" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
      <Typography variant="caption" color="textSecondary" style={{ lineHeight: '24px' }}>
        Plugins enforced:
      </Typography>
      {active.map(p => (
        <Chip key={p} size="small" label={p} style={{ backgroundColor: '#2e7d32', color: '#ffffff', fontFamily: 'monospace' }} />
      ))}
    </Box>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <Box display="flex" style={{ gap: 8, marginTop: 4 }}>
      <Typography variant="caption" color="textSecondary" style={{ minWidth: 72 }}>{label}</Typography>
      <Typography variant="caption" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{value}</Typography>
    </Box>
  );
}

export function StatusBanner({ client }: { client: RebacExplorerClient }) {
  const theme = useTheme();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    client.getStatus()
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [client]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8, padding: '10px 16px' }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="textSecondary">Carregando status de autorização…</Typography>
      </Box>
    );
  }

  if (!status) return null;

  const cfg = MODE_CONFIG[status.mode];
  const isDark = theme.palette.type === 'dark';
  const surfaceBg = isDark ? theme.palette.background.paper : theme.palette.background.default;

  return (
    <Paper
      elevation={0}
      style={{
        border: `1px solid ${cfg.borderColor}`,
        borderRadius: 4,
        backgroundColor: surfaceBg,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ padding: '10px 16px', cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <InfoOutlinedIcon style={{ color: cfg.iconColor, fontSize: 18 }} />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Mecanismo de autorização:
          </Typography>
          <Chip
            size="small"
            label={cfg.label}
            style={{ backgroundColor: cfg.chipBg, color: cfg.chipText, fontWeight: 700, fontSize: '0.75rem' }}
          />
          {status.mode === 'enforce' && <EnforcedPlugins enforce={status.enforce} />}
        </Box>
        <Tooltip title={expanded ? 'Recolher' : 'Ver detalhes'}>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded detail */}
      <Collapse in={expanded}>
        <Divider />
        <Box style={{ padding: '12px 16px' }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            {cfg.description}
          </Typography>

          {status.openfgaConfigured && (
            <>
              <DetailRow label="API URL"  value={status.apiUrl} />
              <DetailRow label="Store ID" value={status.storeId} />
              {status.modelId && <DetailRow label="Model ID" value={status.modelId} />}
              {status.mode === 'shadow' && (
                <Box
                  style={{
                    marginTop: 12,
                    padding: '8px 12px',
                    borderRadius: 4,
                    border: `1px solid ${cfg.borderColor}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Divergências entre RBAC e OpenFGA são registradas nos logs com o prefixo{' '}
                    <span style={{ fontFamily: 'monospace' }}>rebac_shadow_divergence</span>.
                    Nenhuma decisão de acesso é afetada.
                  </Typography>
                </Box>
              )}
              {status.mode === 'enforce' && (
                <Box style={{ marginTop: 8 }}>
                  <EnforcedPlugins enforce={status.enforce} />
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
